import React, { useState, useEffect, useMemo } from "react";

import Typography from "@mui/material/Typography";
import MuiAppBar, { AppBarProps as MuiAppBarProps } from "@mui/material/AppBar";
import { styled } from "@mui/material/styles";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import {
  Box,
  Button,
  CircularProgress,
  Tooltip,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useLocation } from "react-router-dom";

import { UpgradeBoltIcon } from "./components/UpgradeBoltIcon";
import AccountMenu from "./components/AccountMenu";
import PricingPlaneDialog from "../PricingPlansDialog";
import { usersService } from "./services";
import calculateUsedCreditsInPercentage from "../../../utils/calculateCreditUsage";
import { useUserState } from "../../../store";
import { RoleName } from "../enums/RoleName";
import { setUserContext } from "../../../analytics";
import Breadcrumbs from "../Breadcrumbs";
import { BRAND_COLORS } from "../../../constants";

interface Props {
  pageName: string;
  onMobileMenuClick?: () => void;
  showLogo?: boolean;
  headerLeftContent?: React.ReactNode;
}

interface AppBarProps extends MuiAppBarProps {
  open?: boolean;
}
interface CreditSubscription {
  status: string;
  pricing_cycle: string;
  start_date: string;
  end_date: string;
  available_credits: number;
  pricing_plan: {
    id: string;
    name: string;
    description: string;
    created_at: string;
    credits_per_month: number;
  };
}

interface CreditOrganization {
  id: string;
  name: string;
  created_at: string;
  owner_id: string;
  subscription: CreditSubscription;
}

interface CreditUser {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  created_at: string;
  organization: CreditOrganization;
  roles: string[];
}

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})<AppBarProps>(({ theme }) => ({
  zIndex: theme.zIndex.drawer + 1,
  width: "100%",
  marginLeft: 0,
  position: "relative",
  backgroundColor: theme.palette.background.paper,
  color: theme.palette.text.primary,
  borderBottom: `1px solid ${theme.palette.divider}`,
  boxShadow: "none",
}));

export default function Headerbar({
  pageName,
  onMobileMenuClick,
  showLogo = false,
  headerLeftContent,
}: Props) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const location = useLocation();

  const [openPricingPlanDialog, setOpenPricingPlanDialog] = useState(false);
  const { updateUser } = useUserState();

  const handleOpenPricingPlanDialog = () => {
    setOpenPricingPlanDialog(!openPricingPlanDialog);
  };

  const { data, isSuccess } = useQuery({
    queryKey: ["authenticated-user"],
    queryFn: () => usersService.fetchAuthenticardUser(),
  });

  useEffect(() => {
    if (isSuccess && data) {
      updateUser(data);
      setUserContext({
        id: data.id,
        roles: data.roles,
        subscriptionPlan: data.organization?.subscription?.pricing_plan?.name,
        subscriptionStatus: data.organization?.subscription?.status,
      });
    }
  }, [isSuccess, data, updateUser]);

  const creditData = data as CreditUser | undefined;

  const availableCredits =
    creditData?.organization.subscription?.available_credits || 0;

  const totalCreditsPerMonth =
    creditData?.organization.subscription?.pricing_plan?.credits_per_month || 0;

  const percentageUsed =
    totalCreditsPerMonth > 0
      ? calculateUsedCreditsInPercentage(totalCreditsPerMonth, availableCredits)
      : 0;

  const formatCredits = (credits: number) => {
    if (credits >= 1000) {
      return `${(credits / 1000).toFixed(1)}k`;
    }
    return credits.toString();
  };

  const remainingCredits = formatCredits(availableCredits);

  const showCreditUsage = totalCreditsPerMonth > 0;

  const showCreditsAndUpgrade =
    creditData?.roles?.some((r) =>
      [RoleName.ADMIN, RoleName.EDITOR].includes(r as RoleName),
    ) ?? false;

  const planName =
    creditData?.organization?.subscription?.pricing_plan?.name || null;

  const autoBreadcrumbItems = useMemo(() => {
    const prettify = (value: string) =>
      value
        .replace(/[-_]/g, " ")
        .replace(/\b\w/g, (char) => char.toUpperCase());

    const looksLikeId = (value: string) =>
      /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
        value,
      ) ||
      /^[a-f0-9]{24}$/i.test(value) ||
      /^\d+$/.test(value);

    const segments = location.pathname.split("/").filter(Boolean);

    if (!segments.length) {
      return [{ label: pageName || "Home", path: "/" }];
    }

    let pathAcc = "";
    const items = segments.reduce<Array<{ label: string; path: string }>>(
      (acc, segment, index) => {
        pathAcc += `/${segment}`;
        const isLast = index === segments.length - 1;

        if (!isLast && looksLikeId(segment)) {
          return acc;
        }

        acc.push({
          label: isLast ? pageName : prettify(segment),
          path: pathAcc,
        });
        return acc;
      },
      [],
    );

    return items.length
      ? items
      : [{ label: pageName, path: location.pathname }];
  }, [location.pathname, pageName]);

  const leftHeaderContent = headerLeftContent || (
    <Breadcrumbs items={autoBreadcrumbItems} showHome={false} inHeader />
  );

  return (
    <AppBar position="absolute">
      <Toolbar
        sx={{
          pr: { xs: 1.5, sm: 2, md: 2.5 },
          pl: { xs: 1.5, sm: 2, md: 2.5 },
          minHeight: { xs: "64px", sm: "64px", md: "64px" },
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
        }}
      >
        {/* Left side: mobile menu + page title */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            minWidth: 0,
            flex: 1,
          }}
        >
          {isMobile && (
            <IconButton
              edge="start"
              aria-label="open mobile menu"
              onClick={onMobileMenuClick}
              size="small"
              sx={{
                color: theme.palette.text.secondary,
                flexShrink: 0,
                "&:hover": { backgroundColor: theme.palette.action.hover },
              }}
            >
              <MenuIcon sx={{ fontSize: 22 }} />
            </IconButton>
          )}

          {showLogo && !headerLeftContent ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <img
                src="/logo.png"
                alt=""
                style={{
                  width: 32,
                  height: 32,
                  display: "block",
                  flexShrink: 0,
                  objectFit: "contain",
                }}
              />
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: "0.9rem", md: "1rem" },
                  color: theme.palette.text.primary,
                  letterSpacing: "-0.01em",
                }}
              >
                Podcast Assistant
              </Typography>
            </Box>
          ) : (
            <Box
              sx={{
                minWidth: 0,
                width: "100%",
                overflow: "hidden",
              }}
            >
              {leftHeaderContent}
            </Box>
          )}
        </Box>

        {/* Right side actions */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: { xs: 0.5, sm: 1 },
            flexShrink: 0,
          }}
        >
          {/* Remaining credits */}
          {isSuccess && creditData && (
            <Tooltip
              title={
                totalCreditsPerMonth > 0
                  ? `${availableCredits.toLocaleString()} of ${totalCreditsPerMonth.toLocaleString()} monthly credits remaining`
                  : `${availableCredits.toLocaleString()} credits available`
              }
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: { xs: 0.5, sm: 1 },
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: "8px",
                  px: { xs: 1, sm: 1.25 },
                  py: 0.5,
                  backgroundColor: "#eff6ff",
                  flexShrink: 0,
                  cursor: "default",
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: "text.secondary",
                    fontWeight: 600,
                    textTransform: "uppercase",
                    fontSize: "10px",
                    letterSpacing: "0.06em",
                    display: { xs: "none", sm: "block" },
                  }}
                >
                  Credits
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  {showCreditUsage && (
                    <Box
                      sx={{
                        position: "relative",
                        width: 28,
                        height: 28,
                        display: { xs: "none", md: "inline-flex" },
                      }}
                    >
                      <CircularProgress
                        variant="determinate"
                        value={100}
                        size={28}
                        thickness={4}
                        sx={{ color: theme.palette.grey[200] }}
                      />
                      <CircularProgress
                        variant="determinate"
                        value={percentageUsed}
                        size={28}
                        thickness={4}
                        sx={{
                          color: BRAND_COLORS.primary,
                          position: "absolute",
                          left: 0,
                          top: 0,
                        }}
                      />
                    </Box>
                  )}
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 700,
                      color: BRAND_COLORS.primary,
                      fontSize: { xs: "0.75rem", sm: "0.875rem" },
                      lineHeight: 1.2,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {remainingCredits}
                  </Typography>
                </Box>
              </Box>
            </Tooltip>
          )}

          {/* Upgrade button — pill shape, outline bolt icon */}
          {showCreditsAndUpgrade &&
            (isMobile ? (
              <Tooltip title="Upgrade">
                <IconButton
                  onClick={handleOpenPricingPlanDialog}
                  size="small"
                  sx={{
                    backgroundColor: BRAND_COLORS.primary,
                    color: "#fff",
                    borderRadius: "10px",
                    width: 40,
                    height: 40,
                    "&:hover": { backgroundColor: BRAND_COLORS.primaryDark },
                  }}
                >
                  <UpgradeBoltIcon sx={{ fontSize: 22 }} />
                </IconButton>
              </Tooltip>
            ) : (
              <Button
                onClick={handleOpenPricingPlanDialog}
                variant="contained"
                disableElevation
                startIcon={
                  <UpgradeBoltIcon sx={{ fontSize: 18, color: "#fff" }} />
                }
                sx={{
                  backgroundColor: BRAND_COLORS.primary,
                  color: "#fff",
                  borderRadius: "10px",
                  px: 2,
                  py: 1,
                  fontSize: "0.775rem",
                  fontWeight: 600,
                  textTransform: "none",
                  letterSpacing: "0.01em",
                  boxShadow: "none",
                  gap: 0.75,
                  "& .MuiButton-startIcon": { marginRight: 0 },
                  "&:hover": {
                    backgroundColor: BRAND_COLORS.primaryDark,
                    boxShadow: "none",
                  },
                }}
              >
                Upgrade
              </Button>
            ))}

          {/* Divider between Upgrade and account */}
          {showCreditsAndUpgrade && (
            <Box
              sx={{
                width: "1px",
                height: 28,
                backgroundColor: "divider",
                mx: 0.25,
                display: { xs: "none", sm: "block" },
              }}
            />
          )}

          {/* Account menu */}
          <AccountMenu
            userData={creditData}
            roles={creditData?.roles}
            planName={planName}
          />
        </Box>
      </Toolbar>
      <PricingPlaneDialog
        handleOpenPricingPlanDialog={handleOpenPricingPlanDialog}
        open={openPricingPlanDialog}
      />
    </AppBar>
  );
}
